import {MergeabilityFailureIcon} from '@github-ui/custom-icons/MergeabilityFailureIcon'
import {Banner, Link} from '@primer/react'
import {useState} from 'react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {shouldShowAgentMerge} from '@github-ui/mergebox'

import {ResolveConflictsButton} from '@github-ui/mergebox/components/sections/conflicts/ResolveConflictsButton'
import {ResolveConflictsButtonAndDialog} from '@github-ui/mergebox/components/sections/stacks-merge-conflicts-and-rebase-section/ResolveConflictsButtonAndDialog'
import {ForYouItem} from '../shared/ForYouItem'
import {type MergeabilityData, useMergeabilityData} from './use-mergeability-data'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useOpenMergeBoxPanel} from '../../mergebox/use-go-to-mergebox'
import {OverviewMergeControl} from './shared/OverviewMergeControl'
import {useFeatureState} from '../../../../hooks/use-feature-state'
import {useOverviewCommentComposerRef} from '../../OverviewCommentComposerContext'
import {useLayoutCanComment} from '../../../../stores/layout-selectors'
import {useNavigate} from '@github-ui/react-router'
import {routeToCommentsSection, useCurrentPathParams} from '../../data/use-comments-routing'
import {ViewMode} from '../../../comments/utils/view-mode'

const RESOLVE_CONFLICTS_PROMPT = '@copilot resolve the merge conflicts in this pull request'

type MergeConflictsData = MergeabilityData & {
  conflictsSection: MergeabilityData['conflictsSection'] & {
    conflictsCondition: NonNullable<MergeabilityData['conflictsSection']['conflictsCondition']>
  }
}

export function shouldShowMergeConflicts(data: ReturnType<typeof useMergeabilityData>): data is MergeConflictsData {
  const conflictsSection = data?.conflictsSection
  const isConflictState =
    conflictsSection?.sectionStatus === 'HAS_CONFLICTS' || conflictsSection?.sectionStatus === 'HAS_REBASE_CONFLICTS'

  return (
    !!data &&
    !data.isClosedOrMerged &&
    // if the conflits section is stacked but has explicit conflicts, we still want to render this
    // for you item. shouldRender always returns false if the PR is stacked, so we need to check if
    // the PR itself has conflicts with its base branch. Because we don't want to change any mergebox
    // behavior, we make this change here.
    (!!conflictsSection?.shouldRender ||
      (!!conflictsSection?.isStacked && conflictsSection?.sectionStatus === 'HAS_CONFLICTS')) &&
    isConflictState &&
    !!conflictsSection.conflictsCondition
  )
}

// Renders when the branch has merge conflicts that must be resolved
export function MergeConflicts() {
  const data = useMergeabilityData()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const openMergeBoxPanel = useOpenMergeBoxPanel()
  const agentMergeFeatureState = useFeatureState('agentMerge')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const composerRef = useOverviewCommentComposerRef()
  const currentUserCanComment = useLayoutCanComment()
  const navigate = useNavigate()
  const pathParams = useCurrentPathParams()

  if (!shouldShowMergeConflicts(data)) return null

  const {mergeBoxPullRequest, conflictsSection, mergeSection, stackSection} = data
  const {sectionStatus, conflictsCondition} = conflictsSection
  const conflictsWebEditorPath = `${mergeBoxPullRequest.resourcePath}/conflicts`
  const showAgentOption =
    currentUserCanComment && mergeSection.canUserPushToBase && !!mergeBoxPullRequest.viewerCanUseCopilotCodingAgent
  const showAgentMerge = shouldShowAgentMerge({
    isOpen: mergeBoxPullRequest.state === 'OPEN',
    isFeatureEnabled: agentMergeFeatureState === 'ENABLED',
    viewerCanUseCopilotCodingAgent: mergeBoxPullRequest.viewerCanUseCopilotCodingAgent,
    repositoryStateCondition: data.mergeRequirements?.conditions.find(
      condition => condition.type === 'PULL_REQUEST_REPO_STATE',
    ),
    userStateCondition: data.mergeRequirements?.conditions.find(
      condition => condition.type === 'PULL_REQUEST_USER_STATE',
    ),
    isUserBlockedFromPushing: mergeSection.isUserBlockedFromPushing,
    isConfirmingMergeInfo: false,
  })
  const sendConflictAction = (
    actionName: 'edit_conflicts_on_web' | 'fix_conflicts_with_copilot' | 'resolve_conflicts_with_agent_merge',
  ) =>
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'merge-conflicts',
      action_name: actionName,
    })
  const prefillConflictResolutionComment = () => {
    if (composerRef?.current) {
      composerRef.current.insertContent(RESOLVE_CONFLICTS_PROMPT, {focus: true})
      return
    }

    // Ensure the composer ref is attached before the deferred insertion runs.
    navigate(routeToCommentsSection(pathParams, ViewMode.IssueComments), {
      flushSync: true,
      preventScrollReset: true,
      preventAutofocus: true,
    })
    setTimeout(() => composerRef?.current?.insertContent(RESOLVE_CONFLICTS_PROMPT, {focus: true}))
  }

  return (
    <ForYouItem
      itemId="merge-conflicts"
      visual={{kind: 'icon', icon: MergeabilityFailureIcon, tone: 'danger', glyphSize: 12}}
      title={'Merge conflicts'}
      subText={
        sectionStatus === 'HAS_CONFLICTS' || errorMessage ? (
          <>
            {sectionStatus === 'HAS_CONFLICTS' && (
              <span>
                Resolve the{' '}
                <Link as="button" inline onClick={event => openMergeBoxPanel(event.currentTarget)}>
                  merge conflicts
                </Link>{' '}
                before this can merge.
              </span>
            )}
            {errorMessage && (
              <Banner variant="critical">
                <Banner.Title>Could not update Agent Merge</Banner.Title>
                <Banner.Description>{errorMessage}</Banner.Description>
              </Banner>
            )}
          </>
        ) : undefined
      }
      actions={[
        {
          id: 'resolve-conflicts',
          content: conflictsSection.isStacked ? (
            <ResolveConflictsButtonAndDialog stackNumber={stackSection?.stackMetadata?.number ?? 0} size="small" />
          ) : (
            <ResolveConflictsButton
              conflictsWebEditorPath={conflictsWebEditorPath}
              conflictsState={sectionStatus}
              onActionSelect={sendConflictAction}
              onAgentMergeError={setErrorMessage}
              onFixWithCopilot={composerRef ? prefillConflictResolutionComment : undefined}
              showAgentOption={showAgentOption}
              agentMerge={
                showAgentMerge
                  ? {
                      currentHeadSha: mergeBoxPullRequest.headRefOid,
                      viewerLogin: getCurrentUserLogin() ?? null,
                      pullRequestChannel: mergeBoxPullRequest.mergeBoxAliveChannels?.pullRequestChannel,
                    }
                  : undefined
              }
              webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
              size="small"
            />
          ),
        },
        {
          id: 'merge-when-ready',
          content: <OverviewMergeControl data={data} itemName="merge-conflicts" />,
        },
      ]}
    />
  )
}
